import { Clock, Play } from 'lucide-react';
import type { Product } from './types';
import { normalizeProductPricing } from './pricingNormalization';

export const timeAttendanceSystem: Product = normalizeProductPricing({
  id: 'time-attendance-system',
  title: 'Time & Attendance System',
  subtitle: 'AI-Powered Workforce Time Tracking, Scheduling & Compliance Platform',
  description: 'Comprehensive time and attendance management platform combining AI-powered scheduling, biometric integration, real-time tracking, overtime management, and UK employment law compliance for BPO contact centres, HR departments, and multi-site enterprises.',
  icon: Clock,
  image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  category: 'Human Resources',
  status: 'Live',
  businessTypes: ['#time-attendance', '#workforce-management', '#scheduling', '#payroll-integration', '#compliance', '#bpo', '#contact-centre', '#hr-technology'],
  features: [
    // Time Tracking & Clocking (8 features)
    'AI-Powered Biometric Time Clock Integration',
    'Multi-Device Clock-In (Mobile, Web, Kiosk, Tablet)',
    'GPS Geofencing & Location Verification',
    'Facial Recognition & Fingerprint Authentication',
    'Real-Time Attendance Dashboard & Monitoring',
    'Buddy Punching Prevention with AI Verification',
    'Offline Mode with Automatic Sync',
    'QR Code & NFC Badge Scanning',

    // Scheduling & Shift Management (8 features)
    'AI-Powered Smart Scheduling & Auto-Rostering',
    'Drag-and-Drop Shift Builder Interface',
    'Skills-Based Shift Allocation',
    'Open Shift Marketplace & Self-Service Swap',
    'Split Shift & Multi-Location Support',
    'Shift Pattern Templates & Rotation Planning',
    'Availability Management & Preference Tracking',
    'Real-Time Schedule Conflict Detection',

    // Leave & Absence Management (7 features)
    'Comprehensive Leave Request & Approval Workflow',
    'Holiday Entitlement Calculator (UK Statutory)',
    'Bradford Factor Absence Tracking',
    'TOIL & Flexi-Time Accrual Management',
    'Sick Leave Documentation & Return-to-Work',
    'Annual Leave Calendar & Team Coverage View',
    'Absence Pattern Detection & Alerts',

    // Overtime & Compliance (7 features)
    'Automatic Overtime Calculation & Rules Engine',
    'Working Time Regulations (WTR) Compliance',
    'Break Time Enforcement & Monitoring',
    'Maximum Hours Alerts & Fatigue Management',
    'UK National Minimum Wage Compliance',
    'Junior Worker & Young Worker Hour Restrictions',
    'Audit-Ready Compliance Reporting',

    // Payroll Integration (8 features)
    'Seamless Payroll System Integration',
    'Automated Timesheet Export & Approval',
    'Multi-Rate Pay Calculations (Weekends, Nights)',
    'Expense & Mileage Integration',
    'HMRC RTI-Ready Data Export',
    'Pension Auto-Enrolment Hours Tracking',
    'Contractor & Agency Worker Management',
    'Cost Centre & Project Code Allocation',

    // Analytics & Intelligence (8 features)
    'AI Carly Smart Search Across All Time Data',
    'Predictive Absence Forecasting',
    'Labour Cost Analytics & Budgeting',
    'Productivity & Efficiency Metrics',
    'Benchmarking & KPI Dashboards',
    'Custom Report Builder & Scheduling',
    'Trend Analysis & Historical Comparisons',
    'Real-Time Business Intelligence',

    // Enterprise Security (6 features)
    'End-to-End Data Encryption & Secure APIs',
    'Single Sign-On (SSO) & Multi-Factor Authentication',
    'Role-Based Access Control & User Management',
    'GDPR & Data Protection Compliance',
    'SOC 2 & ISO 27001 Certified Infrastructure',
    '24/7 Security Monitoring & Threat Detection',
  ],
  pricing: { setup: 28500, monthly: 895 },
  color: 'from-cyan-600 to-blue-700',
  demoConfig: {
    type: 'interactive',
    url: '/time-attendance/demo',
    title: 'Experience the Complete Platform',
    description: 'Explore our AI-powered time and attendance system with real-time tracking, smart scheduling, and compliance management',
    buttonText: 'Launch Time & Attendance Demo',
    icon: Play,
    buttonColor: 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700',
  },
});

